import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import Modal from './Modal';
import { salesTargetsAPI } from '../services/api';
import { FormErrorBanner } from './FormErrorBanner';
import { BlockLoader } from './loading';
import type { SalesField } from '../pages/salesTargets/types';

/**
 * Extra details recorded alongside a sales figure — the sales twin of store and
 * employee custom fields.
 *
 * ── WHY SALES HAS THESE AND TARGETS DOES NOT ─────────────────────────────────
 * A target is one number: what must be sold. A sale is an EVENT, and projects
 * want to record things about it that no other project wants — an invoice
 * number, a channel, a promo code, a return reason. Hard-coding five columns
 * would answer that once and be wrong for the next project, so the SHAPE is
 * configured instead: a field defined here appears on the manual form, as a
 * column in the upload template, and in the export.
 *
 * Same field types, same retire-by-flag rule and the same value validation as
 * store custom fields, so an admin who has defined one has defined all three.
 */

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'dropdown', label: 'Pick one' },
  { value: 'multiple_choice', label: 'Pick several' },
];

const CHOICE_TYPES = new Set(['dropdown', 'multiple_choice']);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

const emptyDraft = () => ({
  id: null as string | null,
  label: '',
  fieldType: 'text',
  optionsText: '',
  isRequired: false,
});

export default function SalesFieldsModal({ isOpen, onClose, projectId }: Props) {
  const [fields, setFields] = useState<SalesField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReturnType<typeof emptyDraft> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.listSalesFields(projectId, true);
      setFields(res.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the sales details.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const options = CHOICE_TYPES.has(draft.fieldType)
        ? draft.optionsText.split('\n').map((o) => o.trim()).filter(Boolean)
        : null;
      const body = {
        label: draft.label.trim(),
        fieldType: draft.fieldType,
        options,
        isRequired: draft.isRequired,
      };
      if (draft.id) await salesTargetsAPI.updateSalesField(projectId, draft.id, body);
      else await salesTargetsAPI.createSalesField(projectId, body);
      setDraft(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save the field.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (field: SalesField) => {
    setError(null);
    try {
      await salesTargetsAPI.setSalesFieldActive(projectId, field.id, !field.active);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not change the field.');
    }
  };

  const remove = async (field: SalesField) => {
    setError(null);
    try {
      await salesTargetsAPI.deleteSalesField(projectId, field.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the field.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { setDraft(null); onClose(); }}
      title="Sales details"
      size="lg"
      footer={
        <div className="flex w-full justify-between gap-2">
          {draft ? (
            <>
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || draft.label.trim() === ''}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setDraft(emptyDraft())}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                <Plus className="h-4 w-4" />
                Add a detail
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Done
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {error && <FormErrorBanner message={error} />}

        {draft ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="sf-label">
                What is it called?
              </label>
              <input
                id="sf-label"
                type="text"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="Invoice number, Channel, Return reason…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                This becomes a column in the sales upload sheet, so keep it short and unambiguous.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="sf-type">
                What kind of value?
              </label>
              <select
                id="sf-type"
                value={draft.fieldType}
                onChange={(e) => setDraft({ ...draft, fieldType: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {CHOICE_TYPES.has(draft.fieldType) && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="sf-options">
                  The choices, one per line
                </label>
                <textarea
                  id="sf-options"
                  rows={4}
                  value={draft.optionsText}
                  onChange={(e) => setDraft({ ...draft, optionsText: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  In the upload sheet, a &quot;pick several&quot; value is typed as a comma-separated list.
                </p>
              </div>
            )}

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.isRequired}
                onChange={(e) => setDraft({ ...draft, isRequired: e.target.checked })}
              />
              Required when a sales figure is entered by hand
            </label>
          </div>
        ) : loading ? (
          <BlockLoader className="py-10" />
        ) : fields.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
            <h3 className="text-base font-medium text-gray-700">No extra details yet</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              Sales are recorded as a quantity per product. If this project needs anything else
              alongside that — an invoice number, a channel, a reason — define it here and it
              appears on the form and in the upload sheet.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {fields.map((f) => (
              <div key={f.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${f.active ? '' : 'bg-gray-50'}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {f.label}
                    {f.isRequired && <span className="ml-2 text-xs text-red-600">required</span>}
                    {!f.active && (
                      <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                        retired
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {FIELD_TYPES.find((t) => t.value === f.fieldType)?.label ?? f.fieldType}
                    {f.options && ` · ${f.options.join(', ')}`}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    title={f.active ? 'Retire' : 'Bring back'}
                    onClick={() => toggle(f)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    {f.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => setDraft({
                      id: f.id, label: f.label, fieldType: f.fieldType,
                      optionsText: (f.options ?? []).join('\n'), isRequired: f.isRequired,
                    })}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => remove(f)}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            <p className="px-3 py-2 text-xs text-gray-500">
              Retiring a field hides it from new entries and keeps everything already recorded
              against it. Deleting one is permanent.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
